import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
    // Powerlete (audit JS-08): see #handleNativeClose.
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Powerlete (audit JS-08). Set for the duration of our own closeDialog(), so
   * #handleNativeClose knows the teardown it would otherwise run has already happened -- the
   * dialog.close() call at the end of closeDialog() below fires this same native 'close' event,
   * and without the guard the body/scroll reset and DialogCloseEvent would both fire twice.
   * @type {boolean}
   */
  #closingByComponent = false;

  /**
   * The control that opened the dialog. Powerlete patch, see #restoreFocus().
   * @type {HTMLElement | null}
   */
  #opener = null;

  /**
   * Shows the dialog.
   * @param {Event} [event] - the trigger's event, when called from an `on:click` binding
   */
  showDialog(event) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Powerlete patch (HORIZON-PATCHES.md, 2026-10-25, review R4-05): remember the opener. Safari
    // does not focus a button on click, so showModal() records <body> as the element to return
    // focus to, and after close() focus is lost. An `on:click` binding passes the event, whose
    // target is the trigger; a scripted open falls back to whatever has focus.
    const trigger = event instanceof Event && event.target instanceof HTMLElement ? event.target : document.activeElement;
    this.#opener = trigger instanceof HTMLElement && trigger !== document.body ? trigger : null;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    // dialog.close() fires 'close' as a QUEUED task, not synchronously (measured), so the flag
    // cannot be cleared right after this call -- #handleNativeClose clears it itself, once it
    // actually sees it, further down.
    this.#closingByComponent = true;
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
    this.#restoreFocus();
  };

  /**
   * Powerlete patch (HORIZON-PATCHES.md, 2026-10-25, review R4-05): Chromium returns focus to the
   * opener on close(); Safari leaves it on the now-hidden element inside the dialog, which then
   * falls to <body> (see showDialog). Only steps in when focus was actually lost like that, so a
   * dialog:close listener or another dialog that has taken focus wins.
   */
  #restoreFocus() {
    const opener = this.#opener;
    this.#opener = null;
    const active = document.activeElement;
    if (opener?.isConnected && (!active || active === document.body || this.refs.dialog.contains(active))) {
      opener.focus({ preventScroll: true });
    }
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Powerlete (audit JS-08). A native close never runs closeDialog() above, so nothing reset
   * body's scroll-lock or dispatched DialogCloseEvent: an Android back-gesture close, a
   * dialog.requestClose() call, or a <form method="dialog"> submit each close the <dialog>
   * directly. Left the page position:fixed until the stranded-lock watchdog
   * (powerlete-popup-visibility.liquid) caught it ~800ms later, and cart-drawer.js's own
   * history cleanup -- wired to DialogCloseEvent, not this native event -- never ran either,
   * leaving a pushed history entry behind that made the shopper's next Back look like a no-op.
   *
   * Listens for 'close' only, not 'cancel': 'cancel' fires BEFORE the dialog actually closes
   * (dialog.open is still true), so it is a request the default action can still be prevented,
   * not a completion signal. 'close' fires for every path that actually closes the dialog,
   * including one that started as a 'cancel', so it alone is the correct hook.
   */
  #handleNativeClose = () => {
    if (this.#closingByComponent) {
      this.#closingByComponent = false;
      return;
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.dispatchEvent(new DialogCloseEvent());
    this.#restoreFocus();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
